import { CalendarIcon } from '@heroicons/react/24/outline'
import { PhoneIcon } from '@heroicons/react/24/solid'
import { useState } from 'react'

import { useInviteDelete } from '@/api/models/users/hooks/useInviteDelete'
import { useRepeatInvite } from '@/api/models/users/hooks/useRepeatInvite'
import { DetailedInvite } from '@/api/models/users/lib/invites.types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { cn } from '@/app/utils'
import UserProfileInfo from '@/components/entities/user/ui/UserProfileInfo'
import { AppButton } from '@/components/shared/button'
import { PERMISSIONS_EXPIRES_AT_LABEL } from '@/components/shared/datepicker/model/permissionsExpiresAt'
import { AppInput } from '@/components/shared/input'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { maskPhoneNumber } from '@/utils/maskPhoneNumber'

import { SetUserProfileRoute } from '../../../hooks/useUserProfileRoute'
import { InviteCreationStage, UserProfileScreens } from '../../../lib/types'
import { formatMskDate, formatPermissionsExpiresAt } from '../../../lib/utils'
import { useUserProfileStore } from '../../../model/store'
import { FixedBottomDrawerButton } from '../../FixedBottomDrawerButton'
import {
  PermissionsBlock,
  PermissionsBlockProject
} from '../../PermissionsBlock'
import { RecallInviteModal } from './RecallInviteModal'

type Props = {
  invite: DetailedInvite
  setUserProfileRoute: SetUserProfileRoute
}

const UserInviteScreen = ({ invite, setUserProfileRoute }: Props) => {
  const { setInviteCreationStage, setSentInvitePermissionsExpiresAt } =
    useUserProfileStore()
  const { reachGoal } = useYandexMetrika()

  const [isRecallRequestModalVisible, setIsRecallRequestModalVisible] =
    useState(false)

  const { mutateAsync: deleteInvite } = useInviteDelete()
  const { mutateAsync: repeatInvite } = useRepeatInvite()

  const infoItems = [
    {
      key: 'phone',
      icon: <PhoneIcon />,
      value: maskPhoneNumber(invite.phone)
    },
    {
      key: 'permissionsExpiresAt',
      icon: <CalendarIcon />,
      value: (
        <span data-testid="invite-details-expires-at">
          <span className="text-grey-70">{PERMISSIONS_EXPIRES_AT_LABEL}: </span>
          {formatPermissionsExpiresAt(invite.permissionsExpiresAt)}
        </span>
      )
    }
  ]

  const accessProjects: PermissionsBlockProject[] =
    invite.projects?.map(({ projectId, projectName, personalAccounts }) => ({
      id: projectId,
      name: projectName,
      rows: personalAccounts.map(
        ({ personalAccountId, personalName, permissions }) => ({
          id: personalAccountId,
          premise: personalName,
          permissions: permissions.join(', ')
        })
      )
    })) ?? []

  const onRecallInvite = () => {
    deleteInvite(invite.id).then(() => {
      setUserProfileRoute(UserProfileScreens.Contacts)
      reachGoal(YandexMetrikaEvent.INVITES_DELETE)
    })
  }

  const onRepeatInvite = () => {
    repeatInvite(invite.id).then(() => {
      setSentInvitePermissionsExpiresAt(invite.permissionsExpiresAt)
      setInviteCreationStage(InviteCreationStage.CreationSucceed)
      setUserProfileRoute(UserProfileScreens.InviteCreation)
      reachGoal(YandexMetrikaEvent.CONTACTS_REPEAT)
    })
  }

  return (
    <>
      <div
        className={cn(
          invite.isRevocable && 'mb-[120px]',
          'flex flex-col gap-24 overflow-y-auto px-40 pt-16 lg:px-24 sm:px-16 xxs:gap-20'
        )}
      >
        <UserProfileInfo infoItems={infoItems} />
        {invite.customName && (
          <AppInput
            dataTestId="invite-details-description-input-id"
            className="!h-56 border border-grey-20 bg-white xxs:!h-48"
            inputClassName="opacity-50"
            placeholder="Описание контакта"
            disabled
            defaultValue={invite.customName}
          />
        )}

        <PermissionsBlock
          projects={accessProjects}
          headerDate={
            <p className="text-grey-80 text-16 xs:text-14">
              Дата создания:{' '}
              <span className="font-semibold text-black">
                {formatMskDate(invite.invitedAt)}
              </span>
            </p>
          }
        />
      </div>
      {(invite.isRevocable || invite.isRepeatable) && (
        <>
          <FixedBottomDrawerButton>
            {invite.isRevocable && (
              <AppButton
                fluid
                variant="backless"
                size="large"
                className="!text-red-80 sm:!h-44"
                onClick={() => setIsRecallRequestModalVisible(true)}
              >
                Отозвать приглашение
              </AppButton>
            )}
            {invite.isRepeatable && (
              <AppButton
                fluid
                variant="secondary"
                size="large"
                className="sm:!h-44"
                onClick={onRepeatInvite}
              >
                Повторить приглашение
              </AppButton>
            )}
          </FixedBottomDrawerButton>
          <RecallInviteModal
            visible={isRecallRequestModalVisible}
            onClose={() => setIsRecallRequestModalVisible(false)}
            onSubmit={onRecallInvite}
          />
        </>
      )}
    </>
  )
}

export default UserInviteScreen
